<script setup lang="ts">
import { useElementSize } from '@vueuse/core';
import { onMounted, watch } from 'vue';
import ApStories from '@/components/apostatudo/organisms/Stories.vue';
import { useModalStore } from '@/stores/apostatudo/modal';
import { useStoriesStore } from '@/stores/apostatudo/stories';
import { useSmarticoMessages } from '@/stores/smartico/messages';

const layoutStore = useLayoutStore();
const { isAuthenticated, accessToken } = storeToRefs(useAuthStore());
const { isGameModalOpen } = storeToRefs(useCasinoStore());
const { userCanOnlyWithdraw } = useUserLimits();
const { currentRouteName } = useRoutes();
// Saudação do topo: apelido da Smartico na frente do nome do cadastro. Aqui
// não tem lápis — a saudação é apertada (no mobile divide a linha com o
// VipTag) e a edição fica a um toque, no dropdown de perfil.
const { displayName: firstName } = useSmarticoNickname();
const { isMobile, isDesktop } = useLayout();

const props = defineProps<{
  isRoletasPage: boolean;
}>();

const renderAfterAuth = ref<string>('');

const header = ref<HTMLDivElement | null>(null);

const { height: headerHeight } = useElementSize(
  header,
  { height: 0, width: 0 },
  { box: 'border-box' },
);

watch(headerHeight, (newHeight) => {
  layoutStore.setHeaderHeight(newHeight);
});

watch(isAuthenticated, () => {
  setTimeout(() => {
    renderAfterAuth.value = btoa(accessToken.value);
  }, 100);
});

const { hasStories, hasUnseenStories } = storeToRefs(useStoriesStore());
const modal = useModalStore();
const smarticoMessages = useSmarticoMessages();

onMounted(() => {
  if (isAuthenticated.value) {
    smarticoMessages.loadMessages();
  }
});

watch(isAuthenticated, (val) => {
  if (val) {
    smarticoMessages.loadMessages();
  }
});
</script>

<template>
  <header
    id="divPageHeader"
    ref="header"
    :class="[
      $style.header,
      { hidden: isGameModalOpen },
      props.isRoletasPage
        ? 'bg-black'
        : 'bg-header-bg rounded-b-[9px] -mb-[6px]',
    ]"
  >
    <div
      v-if="!userCanOnlyWithdraw"
      :class="[
        $style.sections,
        props.isRoletasPage ? 'bg-black' : 'bg-header-bg',
      ]"
    >
    </div>

    <div :class="[$style.wrapper]">
      <div
        :class="[$style.content, { [$style.authenticated]: isAuthenticated }]"
      >
        <div :class="$style.leftContent">
          <div
            v-if="!userCanOnlyWithdraw"
            :class="$style.hamburgerMenu"
          >
            <button
              class="text-2xl text-header-texts"
              @click="layoutStore.toggleMenuOpen()"
            >
              <NuxtIcon name="bars" />
            </button>
          </div>

          <div
            v-if="currentRouteName === 'index'"
            class="flex"
            :class="
              isMobile && isAuthenticated
                ? 'h-full flex-col items-start justify-between'
                : 'items-center'
            "
          >
            <div class="flex items-center gap-2">
              <ApHeaderLogo
                :has-stories="hasStories"
                :has-unseen-stories="hasUnseenStories"
                @click-stories="() => modal.open(ApStories)"
              />

              <!-- Desktop: o selo VIP fica colado na logo. No mobile ele desce
                   junto com a saudação, na segunda linha. -->
              <VipTag
                v-if="!isMobile && isAuthenticated"
                :height="18"
              />
            </div>

            <div
              v-if="isMobile && isAuthenticated"
              class="flex items-center gap-1.5 justify-start pb-2"
            >
              <span
                class="text-white font-semibold text-[12px] leading-none max-w-[120px] truncate"
              >
                {{ `Olá ${firstName}!` }}
              </span>

              <VipTag :height="16" />
            </div>
          </div>

          <div
            v-else
            class="flex"
            :class="
              isMobile && isAuthenticated
                ? 'h-full flex-col items-start justify-between'
                : 'items-center'
            "
          >
            <div class="flex items-center gap-2">
              <ApHeaderLogo
                :has-stories="hasStories"
                :has-unseen-stories="hasUnseenStories"
                @click-stories="() => modal.open(ApStories)"
              />

              <!-- Desktop: o selo VIP fica colado na logo. No mobile ele desce
                   junto com a saudação, na segunda linha. -->
              <VipTag
                v-if="!isMobile && isAuthenticated"
                :height="18"
              />
            </div>

            <div
              v-if="isMobile && isAuthenticated"
              class="flex items-center gap-1.5 justify-start pb-2"
            >
              <span
                class="text-white font-semibold text-[12px] leading-none max-w-[120px] truncate"
              >
                {{ `Olá ${firstName}!` }}
              </span>

              <VipTag :height="16" />
            </div>
          </div>

          <HeaderNavButtons
            v-if="isDesktop"
            class="ml-[30px]"
          />
        </div>

        <div :class="$style.authButtons">
          <HeaderButtons />
        </div>
      </div>
    </div>
  </header>
</template>

<style module lang="postcss">
.header {
  @apply relative w-full bg-header-bg lg:pb-0 pt-2 lg:pt-0;
}

.content {
  @apply w-full mx-auto flex px-[10px] py-[5px] gap-2;

  /* Mobile não autenticado */
  @apply justify-between items-center h-[65px];

  /* Mobile autenticado */
  &.authenticated {
    @apply h-[90px] items-center justify-center lg:h-[65px];
  }

  /* Desktop */
  @apply lg:justify-between lg:items-center lg:min-h-[65px] lg:h-[65px];
}

.leftContent {
  /* self-stretch: no mobile logado a coluna logo/"Olá nome!" usa h-full +
     justify-between pra se distribuir na altura do .content, alinhando com
     as duas linhas da coluna da direita (Depositar em cima, saldo embaixo). */
  @apply flex self-stretch lg:items-center xl:pl-0.5 w-1/2 lg:flex-1 lg:w-auto;
}

.authButtons {
  @apply ml-auto pt-0 lg:pt-0 w-1/2 lg:w-auto flex justify-end;
}

.hamburgerMenu {
  @apply hidden lg:flex justify-start items-center text-white uppercase gap-5 relative z-50 mr-[23px] focus:outline-none;
  svg {
    margin-top: 1px;
    width: 19.5px;
  }
}

.sections {
  @apply w-[280px] hidden lg:flex justify-between items-center px-5 absolute top-1.5 left-0;
  @apply select-none;
}

.link {
  @apply w-full text-header-links flex items-center justify-center relative gap-2 py-4 text-sm font-bold uppercase no-underline opacity-50 cursor-pointer;

  &:hover {
    @apply opacity-100;
  }

  svg {
    @apply w-[1.2em] h-[1.2em];
  }

  &.isActive {
    @apply opacity-100;

    &:after {
      content: '';
      @apply h-[3px] bg-header-login-bg absolute -bottom-[.4rem] left-[5px] right-1.5;
    }
  }
}
</style>
